import { parseISO } from "date-fns";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import {
    HISTORY_FIELD_LABELS,
    HistoryField,
} from "@common/constants/history-source-types";
import { pluralizeWithCount } from "@common/helpers/string";
import { TaskPriority, TaskStatus } from "@common/types/task";
import { formatDateForTransaction } from "@helpers/date";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import {
    TASK_PRIORITY_MAP,
    TASK_STATUS_MAP,
} from "@components/tasks/taskListUtils";
import {
    getPreferredSnapshot,
    isHistoryField,
    isRecord,
} from "../../utils/historyDetailsHelpers";
import { HistoryTaskUpdateDetailsRow } from "../views/historyTaskUpdateDetailsViewConfig";

const ARRAY_FIELDS: ReadonlySet<string> = new Set([
    HistoryField.ENTITIES,
    HistoryField.FINANCIAL_DOCUMENTS,
    HistoryField.BLOCKED_BY_TASKS,
    HistoryField.BLOCKING_TASKS,
    HistoryField.RELATED_TASKS,
]);

const ARRAY_FIELD_NOUNS: Partial<Record<HistoryField, string>> = {
    [HistoryField.ENTITIES]: "entity",
    [HistoryField.FINANCIAL_DOCUMENTS]: "document",
    [HistoryField.BLOCKED_BY_TASKS]: "task",
    [HistoryField.BLOCKING_TASKS]: "task",
    [HistoryField.RELATED_TASKS]: "task",
};

export function getTaskFieldLabel(field: string): string | undefined {
    if (!isHistoryField(field)) {
        return undefined;
    }
    return HISTORY_FIELD_LABELS[field];
}

const DISPLAY_NAME_KEYS = ["title", "name", "fileName"] as const;

function getItemDisplayName(item: unknown): string | null {
    if (!isRecord(item)) {
        return null;
    }
    for (const key of DISPLAY_NAME_KEYS) {
        const value = item[key];
        if (typeof value === "string" && value.length > 0) {
            return value;
        }
    }
    return null;
}

function formatArrayValue(field: string, value: unknown): string {
    if (!Array.isArray(value) || value.length === 0) {
        return "-";
    }
    const names = value
        .map(getItemDisplayName)
        .filter((name): name is string => name !== null);
    if (names.length > 0) {
        return names.join(", ");
    }
    const noun = isHistoryField(field)
        ? (ARRAY_FIELD_NOUNS[field] ?? "item")
        : "item";
    return pluralizeWithCount(noun, value.length);
}

function formatDateValue(value: unknown): string {
    if (value === null || value === undefined) {
        return "-";
    }
    if (value instanceof Date) {
        return formatDateForTransaction(value);
    }
    if (typeof value === "string" && value.length > 0) {
        const date = parseISO(value);
        if (!Number.isNaN(date.getTime())) {
            return formatDateForTransaction(date);
        }
        return value;
    }
    return "-";
}

function formatEnumValue(field: string, value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }
    if (field === HistoryField.STATUS) {
        return TASK_STATUS_MAP.get(value as TaskStatus) ?? null;
    }
    if (field === HistoryField.PRIORITY) {
        return TASK_PRIORITY_MAP.get(value as TaskPriority) ?? null;
    }
    return null;
}

function formatTaskRefValue(
    field: string,
    ref: AuditLogDiff["before"],
): string {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    const value = ref.value ?? null;
    if (ARRAY_FIELDS.has(field)) {
        return formatArrayValue(field, value);
    }
    if (field === HistoryField.DUE_DATE) {
        return formatDateValue(value);
    }
    if (field === HistoryField.ESTIMATED_TIME) {
        return typeof value === "number" ? formatDuration(value) || "-" : "-";
    }
    const enumLabel = formatEnumValue(field, value);
    if (enumLabel !== null) {
        return enumLabel;
    }
    if (value === null || value === undefined) {
        return "-";
    }
    if (typeof value === "boolean") {
        return value ? "yes" : "no";
    }
    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }
    return JSON.stringify(value);
}

function getNonEmptyString(
    snapshot: Record<string, unknown> | undefined,
    key: string,
): string | undefined {
    if (!snapshot) {
        return undefined;
    }
    const candidate = snapshot[key];
    if (typeof candidate === "string" && candidate.length > 0) {
        return candidate;
    }
    return undefined;
}

function getTaskTitleFromMetadata(log: HistoryDetailsItem): string | undefined {
    const preferred = getPreferredSnapshot(log);
    return (
        getNonEmptyString(preferred, "title") ??
        getNonEmptyString(preferred, "label")
    );
}

function getTaskTitleFromDiff(log: HistoryDetailsItem): string | undefined {
    const titleDiff = log.diff.find((d) => d.field === "title");
    if (!titleDiff) {
        return undefined;
    }
    const ref =
        log.changeType === AuditLogChangeType.DELETE
            ? titleDiff.before
            : titleDiff.after;
    if (typeof ref.value === "string" && ref.value.length > 0) {
        return ref.value;
    }
    if (typeof ref.valueLabel === "string" && ref.valueLabel.length > 0) {
        return ref.valueLabel;
    }
    return undefined;
}

function resolveTaskDisplayTitle(log: HistoryDetailsItem): string {
    const resourceTitle = log.resource?.title;
    if (typeof resourceTitle === "string" && resourceTitle.length > 0) {
        return resourceTitle;
    }
    const resourceLabel = log.resource?.label;
    if (typeof resourceLabel === "string" && resourceLabel.length > 0) {
        return resourceLabel;
    }
    const metadataTitle = getTaskTitleFromMetadata(log);
    if (metadataTitle) {
        return metadataTitle;
    }
    const diffTitle = getTaskTitleFromDiff(log);
    if (diffTitle) {
        return diffTitle;
    }
    return "Task";
}

export function buildTaskUpdateDetailsRow(
    log: HistoryDetailsItem,
): HistoryTaskUpdateDetailsRow {
    const taskTitle = resolveTaskDisplayTitle(log);
    const base = {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        taskTitle,
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };

    if (
        log.changeType === AuditLogChangeType.CREATE ||
        log.changeType === AuditLogChangeType.DELETE
    ) {
        const isDelete = log.changeType === AuditLogChangeType.DELETE;
        return {
            ...base,
            changedFieldLabel: null,
            changedField: null,
            previous: isDelete ? taskTitle : "-",
            modified: isDelete ? "-" : taskTitle,
            previousValue: null,
            modifiedValue: null,
        };
    }

    const uniqueFields = Array.from(
        new Set(log.diff.map((diff) => diff.field)),
    );
    if (uniqueFields.length === 1) {
        const [field] = uniqueFields;
        const diff = log.diff[0];
        return {
            ...base,
            changedFieldLabel: getTaskFieldLabel(field) ?? null,
            changedField: isHistoryField(field) ? field : null,
            previous: formatTaskRefValue(field, diff.before),
            modified: formatTaskRefValue(field, diff.after),
            previousValue: diff.before.value,
            modifiedValue: diff.after.value,
        };
    }

    return {
        ...base,
        changedFieldLabel: null,
        changedField: null,
        previous: log.diff
            .map((diff) => {
                const fieldLabel = getTaskFieldLabel(diff.field) ?? diff.field;
                return `${fieldLabel}: ${formatTaskRefValue(diff.field, diff.before)}`;
            })
            .join(" · "),
        modified: log.diff
            .map((diff) => {
                const fieldLabel = getTaskFieldLabel(diff.field) ?? diff.field;
                return `${fieldLabel}: ${formatTaskRefValue(diff.field, diff.after)}`;
            })
            .join(" · "),
        previousValue: null,
        modifiedValue: null,
    };
}
